import React, { useState } from "react";
import { Stack, Box, Chip, IconButton, useTheme } from "@mui/material";
import { Button, Badge, Container, Avatar, Card, DSTypography } from "../design-system";
import { NavBar } from "../navBar";
import { SocialNetworks, CurrentlyPlaying, CVModal } from "./components";
import { AudioWaveform } from "../common/AudioWaveform";
import avatarImage from "../../assets/img/avatar.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShuffle, faFile } from "@fortawesome/free-solid-svg-icons";
import moodsData from "../../data/moods.json";

export type Mood = {
  id: string;
  label: string;
  artist: string;
  embedUrl: string;
  background: string;
};

export const Profile = () => {
  const theme = useTheme();
  const moods: Mood[] = moodsData.moods;
  const [currentMood, setCurrentMood] = useState<Mood>(moods[0]);
  const [cvModalOpen, setCvModalOpen] = useState(false);

  const shuffleMood = () => {
    const others = moods.filter((m) => m.id !== currentMood.id);
    const next = others[Math.floor(Math.random() * others.length)];
    setCurrentMood(next || currentMood);
  };

  const profileStyles = theme.custom.componentStyles.profile;

  return (
    <Stack spacing={theme.spacing(profileStyles.containerSpacing)}>
      {/* Now Playing / Hero */}
      <Container
        variant="default"
        sx={{
          backgroundImage: theme.custom.gradients.hero,
          borderColor: theme.custom.colorOpacity.teal.light,
        }}
      >
        <Stack spacing={theme.spacing(profileStyles.heroSpacing)}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={theme.spacing(profileStyles.heroHeaderSpacing)}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={theme.spacing(profileStyles.heroBadgeSpacing)}
              flexWrap="wrap"
            >
              <Badge label="Now Playing" variant="teal" />
              <Box
                sx={{
                  width: profileStyles.audioWaveformWidth,
                  maxWidth: profileStyles.audioWaveformMaxWidth,
                }}
              >
                <AudioWaveform
                  height={profileStyles.audioWaveformHeight}
                  color="primary.main"
                  bars={12}
                />
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={theme.spacing(profileStyles.heroSpacing)}
              flexWrap="wrap"
            >
              <Badge label="Music Lover" variant="gold" />
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={theme.spacing(profileStyles.heroSpacing)}
            alignItems="center"
          >
            <Avatar src={avatarImage} alt="Lam Nguyen" size="medium" />

            {/* Track info */}
            <Stack
              spacing={theme.spacing(profileStyles.trackInfoSpacing)}
              sx={{ flex: 1, minWidth: 0 }}
            >
              <DSTypography
                variant="hero"
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                Lam Nguyen — Software Engineer
              </DSTypography>
              <DSTypography
                variant="description"
                sx={{
                  maxWidth: { xs: "100%", sm: theme.spacing(profileStyles.descriptionMaxWidth) },
                  lineHeight: profileStyles.descriptionLineHeight,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                Frontend engineer with a love for clean interfaces, good architecture, and even
                better music.
              </DSTypography>

              {/* Professional skills */}
              <Stack
                direction="row"
                spacing={theme.spacing(profileStyles.hobbiesSpacing)}
                flexWrap="wrap"
                sx={{ gap: theme.spacing(profileStyles.hobbiesSpacing) }}
              >
                {[{ label: "React" }, { label: "TypeScript" }, { label: "Enterprise Client" }].map(
                  (item) => (
                    <Chip
                      key={item.label}
                      label={item.label}
                      size="small"
                      sx={theme.custom.componentStyles.hobbyChip}
                    />
                  )
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Container>

      {/* Navigation */}
      <Box sx={{ mt: theme.spacing(profileStyles.navMarginTop) }}>
        <NavBar />
      </Box>

      {/* Social Networks */}
      <Box sx={{ mt: theme.spacing(profileStyles.socialMarginTop) }}>
        <SocialNetworks />
      </Box>

      {/* Resume CTA */}
      <Box sx={{ mt: theme.spacing(profileStyles.resumeMarginTop) }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setCvModalOpen(true)}
          startIcon={<FontAwesomeIcon icon={faFile} size="sm" />}
          aria-label="View full resume"
          sx={{
            textTransform: "none",
            fontWeight: profileStyles.resumeButton.fontWeight,
            fontSize: profileStyles.resumeButton.fontSize,
            color: "text.primary",
            bgcolor: "background.paper",
            borderColor: "divider",
            borderWidth: 1,
            borderRadius: theme.custom.borderRadius.md,
            px: theme.spacing(profileStyles.resumeButton.padding.x),
            py: theme.spacing(profileStyles.resumeButton.padding.y),
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "background.paper",
              color: "primary.main",
            },
          }}
        >
          View full resume
        </Button>
      </Box>

      {/* CV Modal */}
      <CVModal open={cvModalOpen} onClose={() => setCvModalOpen(false)} />

      {/* Current mood embed */}
      <Card
        variant="default"
        sx={{
          mt: theme.spacing(profileStyles.moodCard.marginTop),
          p: theme.spacing(profileStyles.moodCard.padding),
          display: "grid",
          gap: theme.spacing(profileStyles.moodCard.gap),
          backgroundImage: currentMood.background ?? "none",
        }}
      >
        <Stack
          spacing={theme.spacing(profileStyles.moodHeaderSpacing)}
          direction="row"
          alignItems="center"
        >
          <DSTypography variant="overline" color={"white"}>
            current mood
          </DSTypography>
          <IconButton
            size="small"
            onClick={shuffleMood}
            sx={{
              color: "secondary.main",
              "&:hover": { color: "secondary.dark", backgroundColor: "transparent" },
              p: theme.spacing(profileStyles.moodIconButtonPadding),
            }}
            aria-label="Shuffle mood"
          >
            <FontAwesomeIcon icon={faShuffle} />
          </IconButton>
        </Stack>
        <Box
          component="iframe"
          src={currentMood.embedUrl}
          title={currentMood.label}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          sx={{
            border: 0,
            width: "100%",
            height: profileStyles.moodIframeHeight,
            display: "block",
          }}
        />
      </Card>

      {/* Currently Playing */}
      <CurrentlyPlaying />
    </Stack>
  );
};
